import React from "react";
import { CheckCircle2 } from "lucide-react";
import SectionHeading from "../SectionHeading";
import { detailTabs, formatDate, formatMoney, statusClasses } from "../../hooks/useProjectsData";
import { MiniMetric } from "./ProjectCard";

function DetailRow({ label, value }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-200">{value}</p>
    </div>
  );
}

function LinkedRecordsList({ theme, title, records, emptyText, renderItem }) {
  if (!records.length) {
    return (
      <div className={`rounded-[24px] border border-dashed px-5 py-10 text-center text-sm ${theme === "dark" ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500"}`}>
        {emptyText}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {title ? <p className={`text-sm font-bold ${theme === "dark" ? "text-slate-200" : "text-slate-800"}`}>{title}</p> : null}
      {records.map((record, index) => {
        const item = renderItem(record, index);
        return (
          <div key={record.id || index} className={`rounded-3xl border px-4 py-3 ${theme === "dark" ? "border-slate-800 bg-slate-950/70" : "border-slate-200 bg-white"}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className={`truncate text-sm font-bold ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>{item.title}</div>
                {item.meta ? <div className={`mt-1 text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{item.meta}</div> : null}
              </div>
              {item.right ? <div className={`shrink-0 text-sm font-black ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>{item.right}</div> : null}
            </div>
            {item.footer ? <div className={`mt-2 text-xs ${theme === "dark" ? "text-slate-500" : "text-slate-500"}`}>{item.footer}</div> : null}
          </div>
        );
      })}
    </div>
  );
}

export default function ProjectDetailsPanel({
  theme,
  text,
  selectedProject,
  projectDetails,
  activeTab,
  setActiveTab,
  detailProfit,
  loadingDetails,
}) {
  if (!selectedProject) {
    return (
      <div className={`rounded-[26px] border border-dashed px-6 py-12 text-center ${theme === "dark" ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500"}`}>
        <div className="text-base font-semibold">{text.noProjects}</div>
        <div className="mt-2 text-sm">{text.noProjectsText}</div>
      </div>
    );
  }

  return (
    <>
      <SectionHeading title={text.details} subtitle={selectedProject.name} />

      <div className="mt-4 flex flex-wrap gap-2">
        {detailTabs.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`rounded-2xl px-3 py-2 text-sm font-semibold transition ${activeTab === tab ? theme === "dark" ? "bg-blue-500/15 text-blue-300" : "bg-blue-50 text-blue-700" : theme === "dark" ? "bg-slate-900 text-slate-300 hover:bg-slate-800" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            {text[tab]}
          </button>
        ))}
      </div>

      <div className={`mt-4 rounded-[28px] border p-4 md:p-5 ${theme === "dark" ? "border-slate-800 bg-slate-900/70" : "border-slate-200 bg-slate-50/70"}`}>
        {loadingDetails ? (
          <div className={`rounded-[24px] border border-dashed px-5 py-10 text-center text-sm ${theme === "dark" ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500"}`}>
            {text.liveSync}...
          </div>
        ) : activeTab === "overview" ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold ${statusClasses(selectedProject.status, theme)}`}>{text[selectedProject.status]}</span>
              <span className={`inline-flex items-center gap-2 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}><CheckCircle2 className="h-4 w-4" />{selectedProject.clientName || "—"}</span>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <DetailRow label={text.address} value={selectedProject.address || "—"} />
              <DetailRow label={text.startDate} value={formatDate(selectedProject.startDate)} />
              <DetailRow label={text.endDate} value={formatDate(selectedProject.endDate)} />
              <DetailRow label={text.totalHours} value={String(projectDetails?.trackedHours || selectedProject.totalHours || 0)} />
              <DetailRow label={text.workersCount} value={String(selectedProject.workersCount || 0)} />
              <DetailRow label={text.materialsCount} value={String(selectedProject.materialsCount || 0)} />
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <MiniMetric label={text.budget} value={formatMoney(selectedProject.budget || projectDetails?.approvedOfferValue || 0)} theme={theme} />
              <MiniMetric label={text.totalCost} value={formatMoney((projectDetails?.laborLiveCost || selectedProject.laborCost || 0) + (projectDetails?.materialTotal || selectedProject.materialCost || 0) + (projectDetails?.expenseTotal || selectedProject.otherCost || 0))} theme={theme} />
              <MiniMetric label={text.estimatedProfit} value={formatMoney(detailProfit)} theme={theme} positive={detailProfit >= 0} />
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <MiniMetric label={text.linkedOffers} value={`${projectDetails?.offers.length || 0} · ${formatMoney(projectDetails?.approvedOfferValue || 0)}`} theme={theme} />
              <MiniMetric label={text.linkedTimesheets} value={`${projectDetails?.timesheets.length || 0} · ${projectDetails?.trackedHours?.toFixed?.(1) || "0.0"}h`} theme={theme} />
              <MiniMetric label={text.linkedInvoices} value={`${projectDetails?.invoices.length || 0} · ${formatMoney(projectDetails?.invoiceTotal || 0)}`} theme={theme} />
              <MiniMetric label={text.materials} value={`${projectDetails?.materials.length || 0} · ${formatMoney(projectDetails?.materialTotal || 0)}`} theme={theme} />
              <MiniMetric label={text.expenses} value={`${projectDetails?.expenses.length || 0} · ${formatMoney(projectDetails?.expenseTotal || 0)}`} theme={theme} />
              <MiniMetric label={text.linkedEvents} value={`${projectDetails?.events.length || 0} ${text.meetings}`} theme={theme} />
            </div>

            <div>
              <p className={`mb-2 text-sm font-bold ${theme === "dark" ? "text-slate-200" : "text-slate-800"}`}>{text.notes}</p>
              <p className={`rounded-3xl border px-4 py-3 text-sm leading-6 ${theme === "dark" ? "border-slate-800 bg-slate-950/70 text-slate-300" : "border-slate-200 bg-white text-slate-600"}`}>{selectedProject.notes || "—"}</p>
            </div>
          </div>
        ) : activeTab === "invoices" ? (
          <LinkedRecordsList
            theme={theme}
            title={text.linkedInvoices}
            emptyText={text.noLinkedRecords}
            records={projectDetails?.invoices || []}
            renderItem={(item) => ({
              title: item.invoiceNumber || item.number || item.invoiceNo || "Invoice",
              meta: item.clientName || item.client?.name || "—",
              right: formatMoney(item.total || item.grandTotal || item.amount || 0),
              footer: `${text.createdAt}: ${formatDate(item.createdAt)}`,
            })}
          />
        ) : activeTab === "timesheets" ? (
          <LinkedRecordsList
            theme={theme}
            title={text.linkedTimesheets}
            emptyText={text.noLinkedRecords}
            records={projectDetails?.timesheets || []}
            renderItem={(item) => ({
              title: item.workerName || "—",
              meta: `${formatDate(item.date)} · ${item.status || "open"}`,
              right: `${Number(item.hours || 0).toFixed(2)}h`,
              footer: `${text.totalCost}: ${formatMoney(item.cost || 0)}`,
            })}
          />
        ) : activeTab === "workers" ? (
          <LinkedRecordsList
            theme={theme}
            title={text.workers}
            emptyText={text.noLinkedRecords}
            records={Array.from(new Map((projectDetails?.timesheets || []).map((item) => [item.workerId || item.workerName, item])).values())}
            renderItem={(item) => ({
              title: item.workerName || "—",
              meta: item.status || "open",
              right: `${Number(item.hours || 0).toFixed(2)}h`,
              footer: item.notes || "—",
            })}
          />
        ) : activeTab === "materials" ? (
          <LinkedRecordsList
            theme={theme}
            title={text.materials}
            emptyText={text.noLinkedRecords}
            records={projectDetails?.materials || []}
            renderItem={(item) => ({
              title: item.name || "—",
              meta: [item.supplier, item.status].filter(Boolean).join(" · ") || "—",
              right: `${Number(item.quantity || 0)} ${item.unit || "pcs"}`,
              footer: `${text.totalCost}: ${formatMoney(Number(item.total || (Number(item.quantity || 0) * Number(item.price || item.unitPrice || 0))))}`,
            })}
          />
        ) : activeTab === "expenses" ? (
          <LinkedRecordsList
            theme={theme}
            title={text.expenses}
            emptyText={text.noLinkedRecords}
            records={projectDetails?.expenses || []}
            renderItem={(item) => ({
              title: item.description || item.category || "—",
              meta: [formatDate(item.date), item.method, item.category].filter(Boolean).join(" · "),
              right: formatMoney(item.amount || item.total || 0),
              footer: item.projectName || selectedProject.name,
            })}
          />
        ) : activeTab === "profit" ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <MiniMetric label={text.budget} value={formatMoney(selectedProject.budget || projectDetails?.approvedOfferValue || 0)} theme={theme} />
            <MiniMetric label={text.totalLinkedRevenue} value={formatMoney((projectDetails?.approvedOfferValue || 0) + (projectDetails?.invoiceTotal || 0))} theme={theme} />
            <MiniMetric label={text.trackedHours} value={`${Number(projectDetails?.trackedHours || 0).toFixed(2)}h`} theme={theme} />
            <MiniMetric label={text.liveCosts} value={formatMoney((projectDetails?.laborLiveCost || selectedProject.laborCost || 0) + (projectDetails?.materialTotal || selectedProject.materialCost || 0) + (projectDetails?.expenseTotal || selectedProject.otherCost || 0))} theme={theme} />
            <MiniMetric label={text.estimatedProfit} value={formatMoney(detailProfit)} theme={theme} positive={detailProfit >= 0} />
            <MiniMetric label={text.invoiceTotal} value={formatMoney(projectDetails?.invoiceTotal || 0)} theme={theme} />
          </div>
        ) : (
          <LinkedRecordsList
            theme={theme}
            title={text.linkedEvents}
            emptyText={text.noLinkedRecords}
            records={projectDetails?.events || []}
            renderItem={(item) => ({
              title: item.title || item.type || "Event",
              meta: item.status || "planned",
              right: formatDate(item.date || item.startDate),
              footer: item.projectName || selectedProject.name,
            })}
          />
        )}
      </div>
    </>
  );
}
